import { useCallback, useContext, useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Box, Button, Typography, TextField, InputLabel } from "@mui/material";
import { IFunctionalLocationValues, IFunctionalLocationForm } from "./FunctionalLocationCreationFormInterfaces";
import { functionalLocationFormValidationSchema } from "validations/schemas";
import { useStyles } from "./FunctionalLocationCreationForm.styles";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { FailureDetailsSettingsContext } from "contexts/FailureDetailsSettings/FailureDetailsSettingsContext";
import CheckIcon from "@mui/icons-material/Check";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { LanguageCodeMap } from "constants/constants";

export const FunctionalLocationCreationForm = ({
    handleClose,
    selectedFunctionalLocationToEdit,
}: IFunctionalLocationForm) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { corporations } = useContext(CorporateContext);
    const { selectedAreaId, selectedFunctionalLocationId } = useContext(FailureDetailsSettingsContext);

    const { addOrInsertFunctionalLocation } = FailureDetailsEntity.UpsertFunctionalLocation();
    const { getExistingTranslationId } = FailureDetailsEntity.CheckForExistingTranslationId();
    const { updateTranslatioName } = FailureDetailsEntity.UpdateTranslationName();
    const { insertTranslatioName } = FailureDetailsEntity.InsertTranslationName();
    const { updateFunctionalLocationData } = FailureDetailsEntity.UpdateFunctionalLocation();
    const [errorMessage, setErrorMessage] = useState("");

    const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);

    const languageIdSelected = useMemo(() => LanguageCodeMap[language as keyof typeof LanguageCodeMap], [language]);
    const existingFunctionalLocationTranslationForLanguage = useMemo(
        () =>
            selectedFunctionalLocationToEdit
                ? selectedFunctionalLocationToEdit.translation.translation_names.find(
                      (tn) => tn.language_id === languageIdSelected,
                  )
                : undefined,
        [selectedFunctionalLocationToEdit, languageIdSelected],
    );
    const resolveErrorMessage = useCallback(
        (error?: ApolloError) => {
            if (
                error?.message.includes("uq_functional_location_name") ||
                error?.message.includes("uq_subordinate_location_name")
            ) {
                return selectedFunctionalLocationId
                    ? t(
                          "failure_details_setup.add_secondary_functional_location_modal.existing_secondary_functional_location_error",
                      )
                    : t("failure_details_setup.add_functional_location_modal.existing_functional_location_error");
            }
            if (error?.message.includes("translation_name_name_language_id_corporate_id_key")) {
                return selectedFunctionalLocationId
                    ? t(
                          "failure_details_setup.add_secondary_functional_location_modal.existing_secondary_functional_location_name_translation_error",
                      )
                    : t(
                          "failure_details_setup.add_functional_location_modal.existing_functional_location_name_translation_error",
                      );
            }
            if (error) {
                return t("failure_details_setup.add_functional_location_modal.generic_error");
            }
            return "";
        },
        [selectedFunctionalLocationId, t],
    );
    const languageCode = language.toLocaleUpperCase();

    const handleAddFunctionalLocationSubmit = useCallback(
        async (values: IFunctionalLocationValues) => {
            setErrorMessage("");
            const trimmedName = values.name.trim();
            const translationNameValues = {
                ...(selectedFunctionalLocationToEdit && {
                    translation_id: selectedFunctionalLocationToEdit.translation.id,
                }),
                language_id: languageIdSelected,
                name: trimmedName,
                corporate_id: corporations[0].corporate_id,
            };

            if (selectedFunctionalLocationToEdit) {
                try {
                    const existingTranslationId = await getExistingTranslationId(translationNameValues);
                    let response;
                    if (
                        existingTranslationId &&
                        existingTranslationId !== selectedFunctionalLocationToEdit.translation.id
                    ) {
                        response = await updateFunctionalLocationData({
                            variables: {
                                functional_location_id: selectedFunctionalLocationToEdit.functional_location_id,
                                changes: { name_translation_id: existingTranslationId },
                            },
                        });
                    } else if (existingFunctionalLocationTranslationForLanguage) {
                        response = await updateTranslatioName({
                            variables: {
                                translation_id: selectedFunctionalLocationToEdit?.translation.id,
                                language_id: languageIdSelected,
                                changes: { name: trimmedName },
                            },
                        });
                    } else {
                        response = await insertTranslatioName({
                            variables: {
                                translation: translationNameValues,
                            },
                        });
                    }
                    if (response) {
                        handleClose();
                    }
                } catch (error) {
                    setErrorMessage(resolveErrorMessage(error as ApolloError));
                    return;
                }
            } else {
                const existingTranslationId = await getExistingTranslationId(translationNameValues);
                const functionalLocationValues = {
                    area_id: selectedAreaId,
                    subordinate_to_id: selectedFunctionalLocationId || null,
                    ...(existingTranslationId
                        ? { name_translation_id: existingTranslationId }
                        : {
                              translation: {
                                  data: {
                                      translation_names: {
                                          data: translationNameValues,
                                      },
                                  },
                              },
                          }),
                };
                try {
                    const response = await addOrInsertFunctionalLocation({
                        variables: { functional_location: functionalLocationValues },
                    });
                    if (response) {
                        handleClose();
                    }
                } catch (error) {
                    setErrorMessage(resolveErrorMessage(error as ApolloError));
                    return;
                }
            }
        },
        [
            addOrInsertFunctionalLocation,
            handleClose,
            selectedAreaId,
            selectedFunctionalLocationId,
            selectedFunctionalLocationToEdit,
            languageIdSelected,
            corporations,
            getExistingTranslationId,
            existingFunctionalLocationTranslationForLanguage,
            insertTranslatioName,
            updateTranslatioName,
            updateFunctionalLocationData,
            resolveErrorMessage,
        ],
    );

    const initFormValues: IFunctionalLocationValues = {
        name:
            selectedFunctionalLocationToEdit && existingFunctionalLocationTranslationForLanguage
                ? existingFunctionalLocationTranslationForLanguage.name
                : "",
        area_id: selectedAreaId!,
        subordinate_to_id: selectedFunctionalLocationId || null,
    };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={functionalLocationFormValidationSchema}
            onSubmit={handleAddFunctionalLocationSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-functional-location-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {`${t(
                                        "failure_details_setup.add_functional_location_modal.name",
                                    )} (${languageCode})`}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-functional-location-name-label"
                                placeholder={
                                    selectedFunctionalLocationToEdit &&
                                    !existingFunctionalLocationTranslationForLanguage
                                        ? t("failure_details_setup.missing_translation_placeholder")
                                        : ""
                                }
                                fullWidth
                                id="outlined-functional-location-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <p
                            style={{ display: errorMessage ? "block" : "none", color: "red" }}
                            className={classes.errorText}
                        >
                            {errorMessage}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t("failure_details_setup.add_functional_location_modal.cancel")}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {<CheckIcon className={classes.icon} />}
                                {t("failure_details_setup.add_functional_location_modal.save")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
